import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Wrench, CheckCircle2, Loader2, AlertTriangle, Package } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function MaintenanceAsset() {
  const { token } = useParams();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ job_description: "", date_completed: today(), performed_by: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.publicAsset(token).then(setAsset).catch(() => setAsset({ valid: false })).finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.job_description.trim()) return;
    setBusy(true);
    try { await api.submitAssetLog(token, form); setDone(true); }
    catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const logAnother = () => { setForm({ job_description: "", date_completed: today(), performed_by: "" }); setDone(false); };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-start px-4 py-8" data-testid="maintenance-asset-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 text-white mb-5">
          <div className="h-10 w-10 rounded-lg bg-orange-500 flex items-center justify-center"><Wrench className="h-5 w-5 text-white" /></div>
          <div>
            <p className="font-display font-bold leading-tight">King Country Self Storage</p>
            <p className="text-xs text-slate-300">Asset Maintenance Log</p>
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl bg-white p-8 flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...</div>
        ) : !asset?.valid ? (
          <div className="rounded-2xl bg-white p-6 text-center space-y-2" data-testid="asset-invalid">
            <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto" />
            <p className="font-semibold text-slate-800">Invalid QR code</p>
            <p className="text-sm text-slate-500">This QR code isn't linked to a known asset. Please scan an official KCSS asset label.</p>
          </div>
        ) : done ? (
          <div className="rounded-2xl bg-white p-8 text-center space-y-3" data-testid="asset-success">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
            <p className="font-display text-xl font-bold text-slate-900">Logged!</p>
            <p className="text-sm text-slate-500">Saved to <span className="font-semibold text-slate-700">{asset.asset_name}</span> ({asset.site}).</p>
            <Button data-testid="asset-log-another" onClick={logAnother} variant="outline" className="w-full">Log another job</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl bg-white p-6 space-y-4 shadow-xl">
            <div className="text-center pb-1">
              <div className="inline-flex items-center gap-1.5 text-slate-800 font-display font-bold text-lg" data-testid="asset-name"><Package className="h-5 w-5 text-orange-600" /> {asset.asset_name}</div>
              <div className="mt-1"><span className="inline-block text-xs font-semibold rounded-full px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200" data-testid="asset-site">{asset.site}</span></div>
              <p className="text-sm text-slate-500 mt-2">Log a repair or servicing job for this asset. No login needed.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Job Description</Label>
              <Textarea data-testid="asset-desc" rows={4} value={form.job_description} onChange={(e) => set("job_description", e.target.value)} placeholder="e.g. Replaced backup batteries" className="text-base" required />
            </div>
            <div className="space-y-1.5">
              <Label>Date Done</Label>
              <Input data-testid="asset-date" type="date" value={form.date_completed} onChange={(e) => set("date_completed", e.target.value)} className="text-base" />
            </div>
            <div className="space-y-1.5">
              <Label>Performed By</Label>
              <Input data-testid="asset-by" value={form.performed_by} onChange={(e) => set("performed_by", e.target.value)} placeholder="Your name or company" className="text-base" />
            </div>
            <Button data-testid="asset-submit" type="submit" disabled={busy || !form.job_description.trim()} className="w-full h-12 text-base bg-orange-600 hover:bg-orange-700">{busy ? "Saving..." : "Submit Log"}</Button>
          </form>
        )}
        <p className="text-center text-[11px] text-slate-400 mt-4">Submissions save straight to this asset's history.</p>
      </div>
    </div>
  );
}
